import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  subscribeToInstallInstructionsModal,
  closeInstallInstructionsModal,
  type InstallPlatform,
} from '../../services/installInstructionsModalService';
import { isIOS, isPreferredInstallBrowser } from '../../services/platformService';
import { Modal } from './Modal';

/**
 * Platform-agnostic "add HomePlated to your Home Screen" instructions modal.
 * Renders iOS (Safari Share sheet) or Android (Chrome ⋮ menu) steps.
 *
 * Deliberately NOT push-framed — unlike the iOS install variant currently
 * living inside PushPermissionModal, this is purely about installing. On
 * Android it's the manual fallback shown when the native install dialog
 * isn't available (see pwaInstallService); on iOS it's the only install path.
 *
 * Platform is auto-detected at render time; the service's `platform` option
 * can force a variant. Mounted once at the app root.
 */
export const InstallInstructionsModal: React.FC = () => {
  const { t } = useTranslation('common');
  const [state, setState] = useState<{ isOpen: boolean; platform?: InstallPlatform }>(
    () => ({ isOpen: false }),
  );

  useEffect(() => subscribeToInstallInstructionsModal(setState), []);

  const platform: InstallPlatform = state.platform ?? (isIOS() ? 'ios' : 'android');
  // Our steps reference Safari (iOS) / Chrome (Android) by name and only work
  // there. If the user is in another browser, lead with a "switch browsers
  // first" notice rather than walking them through a UI they don't have.
  const wrongBrowser = !isPreferredInstallBrowser(platform);

  return (
    <Modal
      isOpen={state.isOpen}
      onClose={closeInstallInstructionsModal}
      size="md"
      zIndexClass="z-50"
    >
      <h2 className="text-lg font-bold text-foreground mb-2">
        {t('install.title')}
      </h2>
      {wrongBrowser && (
        <p className="mb-4 rounded-lg border border-brand-primary/30 bg-brand-primary/5 p-3 text-sm text-foreground">
          {t(platform === 'ios' ? 'install.wrongBrowserIos' : 'install.wrongBrowserAndroid')}
        </p>
      )}
      <ol className="text-sm text-muted-foreground mb-5 space-y-2 list-decimal list-inside">
        {/* steps is a string[] in the locale bundle; each item carries inline
            <strong> markup that Trans renders via the shared components map. */}
        {platform === 'ios'
          ? (t('install.iosSteps', { returnObjects: true }) as string[]).map((_step, i) => (
              <li key={i}>
                <Trans
                  t={t}
                  i18nKey={`install.iosSteps.${i}` as const}
                  components={{ strong: <strong /> }}
                />
              </li>
            ))
          : (t('install.androidSteps', { returnObjects: true }) as string[]).map((_step, i) => (
              <li key={i}>
                <Trans
                  t={t}
                  i18nKey={`install.androidSteps.${i}` as const}
                  components={{ strong: <strong /> }}
                />
              </li>
            ))}
      </ol>
      <div className="flex justify-end">
        <button
          onClick={closeInstallInstructionsModal}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors"
        >
          {t('install.gotIt')}
        </button>
      </div>
    </Modal>
  );
};
